import React, {useMemo} from "react";
import {Outlet, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import UserDiscipline from "../../models/UserDiscipline";
import {useAPI} from "../../config/API";
import PageError from "../../components/errors/PageError";
import ClientViewTeamProfile from "../profiles/TeamProfilePage";
import LoadingBanner from "../../components/LoadingBanner";
import WrippleHelmet from "../../components/WrippleHelmet";

const disciplineCategories = TB.map([...laravel.discipline_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'disciplines', 'display_order'])
    let disciplines = option.disciplines;//TB.filter(option.disciplines, ['is_contractable', true]);

    option.disciplines = TB.map(disciplines, discipline => TB.pick(discipline, ['id', 'name', 'display_order']))
    option.value = option.id;

    return option
});


const disciplines = TB.map(disciplineCategories, category => {
    return {
        ...category,
        options: category.disciplines.map(discipline => {
            return {
                ...discipline,
                value: discipline.id
            }
        })
    }
})

const templateCategories = TB.map([...laravel.project_template_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'project_templates', 'display_order'])
    let project_templates = TB.filter(option.project_templates, {'is_valid': true, 'is_active': true});

    option.project_templates = TB.map(project_templates, template => TB.pick(template, ['id', 'name', 'display_order']))
    option.value = option.id;

    return option
});

const projectTemplates = TB.map(templateCategories, category => {
    return {
        ...category,
        options: category.project_templates.map(template => {
            return {
                ...template,
                value: template.id
            }
        })
    }
})

const experienceLevels = TB.map(laravel.talent_experience_levels, (exp, value) => {
    return {
        ...exp,
        value: parseInt(value),
        name: exp.label + " (" + exp.level + ")",
        display: (
            <span>
                Min Experience:&nbsp;({exp.level})
            </span>
        )
    }
})

// console.log(experienceLevels, 'ExperienceLevels ExperienceLevels')

const ImportanceLevelOptions = TB.map(laravel.importance_levels, (label, value) => ({
    label,
    value,
    isDefault: value === 2,
    level: value
}))
const industries = TB.map(laravel.client_industries, (name, value) => {
    return {name, value: Number(value), id: Number(value)}
});

const BrowseTeamsLayout = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const decodedSearchParams = useMemo(() => {
        let params = [...searchParams.entries()].reduce((acc, [key, val]) => {
            try {
                return { ...acc, [key]: JSON.parse(val) };
            } catch {
                return { ...acc, [key]: val };
            }
        }, {});
        delete params.orientation;
        return params;
    }, [searchParams?.toString()]);

    const hasSetRequiredFilters = useMemo(() => {
        let selectedTemplates = TB.flatMap(
            TB.values(TB.filter(decodedSearchParams, (value, key) => key.includes('project_templates')))
        )
        return selectedTemplates?.length > 0;
    }, [searchParams?.toString()]);

    const {
        data: teamsData,
        isInitialLoading,
        isFetching: isLoading,
        error
    } = useAPI([
        'searchForTeams', { sort_by: 'best_match',...decodedSearchParams }], {
        enabled: searchParams.toString()?.length > 0 && hasSetRequiredFilters,
        keepPreviousData: true
    })

    const criteriaForMatching = useMemo(() => {
        const currentParams = Object.fromEntries([...searchParams]);

        if (!Object.keys(currentParams)?.length) return null;

        let criteria = {};
        TB.map(currentParams, (value, key) => {

            if (key.includes('disciplines')) {
                let roleIds = JSON.parse(value) || [];

                roleIds = TB.filter(roleIds, role => !TB.isNaN(parseInt(role)))
                let roles = TB.filter(TB.flatMap(disciplines, 'options'), dis => roleIds.includes(dis.id));
                criteria.disciplines = TB.uniqBy([
                    ...(criteria.disciplines || []),
                    ...roles
                ], 'id');

                // let scorePrefix = TB.replace(key, '_disciplines', '');
                // if (scorePrefix && TB.find(experienceLevels, ['prefix', scorePrefix])) {
                //     criteria.min_score = TB.find(experienceLevels, ['prefix', scorePrefix])?.value;
                // }
            }else if (key.includes('project_templates')) {
                let templateIds = JSON.parse(value) || [];

                templateIds = TB.filter(templateIds, role => !TB.isNaN(parseInt(role)))
                let templates = TB.filter(TB.flatMap(projectTemplates, 'options'), dis => templateIds.includes(dis.id));
                criteria.project_templates = TB.uniqBy([
                    ...(criteria.project_templates || []),
                    ...templates
                ], 'id');

                // let scorePrefix = TB.replace(key, '_disciplines', '');
                // if (scorePrefix && TB.find(experienceLevels, ['prefix', scorePrefix])) {
                //     criteria.min_score = TB.find(experienceLevels, ['prefix', scorePrefix])?.value;
                // }
            }
            if (key.includes('skills')) {
                let skillIds = JSON.parse(value) || [];
                skillIds = TB.filter(skillIds, skill => !TB.isNaN(parseInt(skill)))
                const skillsByRole = UserDiscipline.skillsForDisciplines(TB.map(TB.flatMap(disciplines, 'options'), 'id')) || [];
                let skills = TB.filter(skillsByRole, skill => skillIds.includes(skill.id));
                let scorePrefix = TB.replace(key, 'skills', '');

                criteria[`${scorePrefix}skills`] = TB.uniqBy([
                    ...(criteria[`${scorePrefix}skills`] || []),
                    ...skills
                ], 'id')
            }else if (key.includes('industries')) {
                let industriesIds = JSON.parse(value) || [];

                industriesIds = TB.filter(industriesIds, industry => !TB.isNaN(parseInt(industry)))
                let industriesSearch = TB.filter(industries, industry => industriesIds.includes(industry.id));
                let scorePrefix = TB.replace(key, 'industries', '');

                criteria[`${scorePrefix}industries`] = TB.uniqBy([
                    ...(criteria[`${scorePrefix}industries`] || []),
                    ...industriesSearch
                ], 'id')
            }



            if (key === 'hired') {
                criteria.hired = JSON.parse(value)
            }

            if (key === 'favorited') {
                criteria.favorited = JSON.parse(value)
            }
            if (key === 'invited') {
                criteria.invited = JSON.parse(value)
            }
            if (key === 'pool_ids') {
                criteria.pool_ids = JSON.parse(value)
                criteria.pool_ids = TB.filter(criteria.pool_ids, pool => !TB.isNaN(parseInt(pool)))
            }
            if (key === 'location' && !TB.isNaN(parseInt(JSON.parse(value)))) {
                criteria.location = parseInt(JSON.parse(value))
            }
            if (key === 'regions') {
                criteria.regions = JSON.parse(value)

                criteria.regions = TB.filter(criteria.regions, regionId => !TB.isNaN(parseInt(regionId)))
            }
            if (key === 'latitude' && !TB.isNaN(parseFloat(JSON.parse(value)))) {
                criteria.latitude = parseFloat(JSON.parse(value))
            }
            if (key === 'longitude' && !TB.isNaN(parseFloat(JSON.parse(value)))) {
                criteria.longitude = parseFloat(JSON.parse(value))
            }
            if (key === 'experience') {
                try {
                    let experience = JSON.parse(value);
                    criteria['experience'] = experience;
                    TB.map(experience, (isChecked, expType) => {
                        criteria[expType] = isChecked;
                    })
                }catch (e) {
                    console.warn('jsonError', e)
                }
            }
        })


        return criteria;
    }, [isLoading, searchParams?.toString()]);

    return (
        <React.Fragment>
            <WrippleHelmet title="Browse Teams" />

            <Outlet context={{
                teamsData,
                hasSetRequiredFilters,
                error,
                criteriaForMatching,
                isInitialLoading,
                isLoading
            }}/>
        </React.Fragment>
    )
}

const BrowseTeamsProfile = (props) => {
    const {
        teamsData,
        hasSetRequiredFilters,
        error,
        criteriaForMatching,
        isLoading
    } = useOutletContext();

    const {teamId} = useParams();

    if (isLoading) {
        return (
            <div className="flex-grow w-full h-full mx-auto">
                <LoadingBanner/>
            </div>
        )
    }
    if (TB.isNaN(parseInt(teamId)) || error || !teamsData?.teams?.length) {
        return (
            <div className="flex-grow w-full h-full mx-auto">
                <PageError/>
            </div>
        )
    }

    return (
        <ClientViewTeamProfile criteria={criteriaForMatching} browseResults={teamsData?.teams}/>
    )
}
export {
    BrowseTeamsProfile
}
export default BrowseTeamsLayout;